import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { toast } from "react-toastify";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import { useForgiveAppointmentScheduleMutation } from "./AppointmentForgiveSchedule-generated.hooks";

interface ContextValue {
  forgiveSchedule: (appointmentUuid: string, options: Options) => Promise<void>;
}

interface AppointmentForgiveScheduleProviderProps {
  children?: ReactNode;
}

interface Options {
  onSuccess?: VoidFunction;
  onError?: (error: unknown) => void;
}

const Context = createContext<ContextValue | undefined>(undefined);

function AppointmentForgiveScheduleProvider(
  props: AppointmentForgiveScheduleProviderProps,
) {
  const { children } = props;

  const [forgiveMutation] = useForgiveAppointmentScheduleMutation();

  const forgiveSchedule = useCallback(
    async (uuid: string, options: Options = {}) => {
      try {
        const { data } = await forgiveMutation({
          variables: {
            uuid,
          },
        });
        const { unpaidReason } = data.forgiveAppointmentSchedule;
        let message = "Schedule forgiven";
        switch (unpaidReason) {
          case "FORGIVEN": {
            message = "Forgiven - client not charged";
            break;
          }
          case "CREDIT_RETURNED": {
            message = "Forgiven - credit returned";
            break;
          }
        }
        toast.success(message, defaultToastOptions);
        options?.onSuccess?.();
      } catch (error) {
        toast.error(extractError(error), defaultToastOptions);
        options?.onError?.(error);
      }
    },
    [forgiveMutation],
  );

  const contextValue = useMemo(() => {
    return { forgiveSchedule };
  }, [forgiveSchedule]);

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
}

export function useAppointmentForgiveSchedule() {
  const contextValue = useContext(Context);
  if (!contextValue) {
    throw new Error(
      "useAppointmentForgiveSchedule must to be used inside an AppointmentForgiveScheduleProvider",
    );
  }
  return contextValue;
}

export default AppointmentForgiveScheduleProvider;
